import {useMemo, useState} from 'react';
import {Plus, Save, X, Pencil, Tags} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Switch
} from '@kroll-resolver-crisp/ui-components-react';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {getApiErrorMessage} from '@/api/client';
import {type ConfigItem, type TopicWriteRequest} from '@/api/config';
import {useCreateTopic, useUpdateTopic} from '@/api/hooks';
import {TablePanel} from '@/components/ui/table-panel';
import {TruncatedText} from '@/components/shared/TruncatedText';

type TopicStatus = TopicWriteRequest['status'];

type Props = {
  readonly initialTopics: ConfigItem[];
  readonly normalisedQuery: string;
};

type Draft = {
  name: string;
  description: string;
  status: TopicStatus;
  start_date: string;
  end_date: string;
};

const COLUMN_COUNT = 5;

const STATUSES: readonly TopicStatus[] = ['active', 'inactive', 'archived'];

const EMPTY_DRAFT: Draft = {
  name: '',
  description: '',
  status: 'active',
  start_date: '',
  end_date: ''
};

function toDraft(topic: ConfigItem): Draft {
  return {
    name: topic.name,
    description: topic.description ?? '',
    status: (STATUSES as readonly string[]).includes(topic.status)
      ? (topic.status as TopicStatus)
      : 'inactive',
    start_date: topic.start_date ?? '',
    end_date: topic.end_date ?? ''
  };
}

function toRequest(draft: Draft): TopicWriteRequest {
  return {
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    status: draft.status,
    start_date: draft.start_date || null,
    end_date: draft.end_date || null
  };
}

/**
 Whether analysts can pick this topic today. The server applies exactly this rule when it serves
 `/api/config/topics`; it is mirrored here so an admin can see at a glance that a topic is active
 but out of its window, which is otherwise indistinguishable from one that is simply available.
 */
function isAvailableToday(topic: ConfigItem): boolean {
  const today = new Date().toISOString().slice(0, 10);
  return (
    topic.status === 'active' &&
    (!topic.start_date || topic.start_date <= today) &&
    (!topic.end_date || topic.end_date >= today)
  );
}

function TopicStatusBadge({status}: {readonly status: string}) {
  if (status === 'active') {
    return <Badge variant="secondary">Active</Badge>;
  }

  return (
    <Badge variant="outline" className="capitalize">
      {status}
    </Badge>
  );
}

/**
 Topic lifecycle management. Unlike the other three reference-data tabs, topics have no upstream
 producer and are administered here — see `AdminConfigController`.

 Local state is patched from each mutation's response rather than refetched, because `ConfigAdmin`
 receives its config items once as props and does not re-subscribe (see the note in `AdminPage`).
 The hooks still invalidate the query keys, so anything that does subscribe stays correct.
 */
export function TopicsAdmin({initialTopics, normalisedQuery}: Props) {
  const [topics, setTopics] = useState<ConfigItem[]>(initialTopics);
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState<Draft>(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);

  const createTopic = useCreateTopic();
  const updateTopic = useUpdateTopic();

  const visible = useMemo(() => {
    const matching = normalisedQuery
      ? topics.filter((t) => t.name.toLowerCase().includes(normalisedQuery))
      : topics;
    const scoped = showArchived ? matching : matching.filter((t) => t.status !== 'archived');
    return [...scoped].sort((a, b) => a.name.localeCompare(b.name));
  }, [topics, normalisedQuery, showArchived]);

  const archivedCount = topics.filter((t) => t.status === 'archived').length;

  const handleCreate = async () => {
    if (!newDraft.name.trim()) {
      return;
    }

    setError(null);
    try {
      const created = await createTopic.mutateAsync(toRequest(newDraft));
      setTopics((previous) => [...previous, created]);
      setNewDraft(EMPTY_DRAFT);
      setCreating(false);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to create topic.'));
    }
  };

  const handleSave = async (id: string) => {
    if (!editDraft.name.trim()) {
      return;
    }

    setError(null);
    try {
      const updated = await updateTopic.mutateAsync({id, body: toRequest(editDraft)});
      setTopics((previous) =>
        // A rename comes back as a different row: the server retires the old name rather than
        // overwriting it, so a submission already tagged with it stays filterable. Keep the
        // retired row in the list, archived, instead of dropping it until the next reload.
        updated.id === id
          ? previous.map((t) => (t.id === id ? updated : t))
          : [
              ...previous.map((t) => (t.id === id ? {...t, status: 'archived' as const} : t)),
              updated
            ]
      );
      setEditingId(null);
    } catch (error_) {
      setError(getApiErrorMessage(error_, 'Failed to update topic.'));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-muted-foreground max-w-2xl text-xs">
          Topics are managed here. Archiving retires a topic without deleting it, so historical
          classifications keep resolving — there is deliberately no delete. Renaming retires the old
          name the same way, rather than overwriting it.
        </p>
        <div className="flex shrink-0 items-center gap-3">
          {archivedCount > 0 ? (
            <div className="flex items-center gap-2">
              <Switch
                size="sm"
                checked={showArchived}
                aria-label={`Show archived (${archivedCount})`}
                onCheckedChange={(checked) => {
                  setShowArchived(checked);
                }}
              />
              <span className="text-muted-foreground text-xs" aria-hidden="true">
                Show archived ({archivedCount})
              </span>
            </div>
          ) : null}
          <Button
            size="sm"
            onClick={() => {
              setCreating(true);
            }}>
            <Plus />
            New topic
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {creating ? (
        // A form sits on the page background, not in a Card - the Card is kept for read-only
        // summaries. The ring-less Card is only here so DraftFields' CardContent/CardFooter spacing
        // resolves, exactly as the in-table editor below does.
        <section
          aria-labelledby="topic-draft-heading"
          className="border-border bg-card rounded-lg border">
          <Card size="sm" className="bg-transparent ring-0">
            <CardHeader>
              <CardTitle id="topic-draft-heading">New topic</CardTitle>
            </CardHeader>
            <DraftFields
              draft={newDraft}
              saving={createTopic.isPending}
              onChange={setNewDraft}
              onSave={handleCreate}
              onCancel={() => {
                setNewDraft(EMPTY_DRAFT);
                setCreating(false);
                setError(null);
              }}
            />
          </Card>
        </section>
      ) : null}

      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Window</TableHead>
              <TableHead className="w-40">Status</TableHead>
              <TableHead className="w-6">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="p-0">
                  <Empty className="py-10">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <Tags />
                      </EmptyMedia>
                      <EmptyTitle>No topics</EmptyTitle>
                      <EmptyDescription>
                        {normalisedQuery
                          ? 'No topic matches this search.'
                          : topics.length > 0
                            ? 'Every topic is archived, and archived topics are hidden.'
                            : 'No topic has been added yet.'}
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : (
              visible.map((topic) =>
                editingId === topic.id ? (
                  <TableRow key={topic.id} className="hover:bg-transparent">
                    <TableCell colSpan={COLUMN_COUNT} className="bg-muted/30 p-0">
                      <Card size="sm" className="bg-transparent ring-0">
                        <DraftFields
                          draft={editDraft}
                          saving={updateTopic.isPending}
                          onChange={setEditDraft}
                          onSave={async () => handleSave(topic.id)}
                          onCancel={() => {
                            setEditingId(null);
                            setError(null);
                          }}
                        />
                      </Card>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={topic.id}>
                    <TableCell className="font-medium">{topic.name}</TableCell>
                    <TableCell className="text-muted-foreground max-w-md">
                      <TruncatedText>{topic.description ?? '—'}</TruncatedText>
                    </TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">
                      {(topic.start_date ?? topic.end_date)
                        ? `${topic.start_date ?? '—'} to ${topic.end_date ?? 'open-ended'}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1">
                        <TopicStatusBadge status={topic.status} />
                        {topic.status === 'active' && !isAvailableToday(topic) ? (
                          <Badge variant="outline">outside dates</Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Edit ${topic.name}`}
                        onClick={() => {
                          setEditDraft(toDraft(topic));
                          setEditingId(topic.id);
                          setError(null);
                        }}>
                        <Pencil />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              )
            )}
          </TableBody>
        </Table>
      </TablePanel>
    </div>
  );
}

function DraftFields({
  draft,
  saving,
  onChange,
  onSave,
  onCancel
}: {
  readonly draft: Draft;
  readonly saving: boolean;
  readonly onChange: (draft: Draft) => void;
  readonly onSave: () => Promise<void>;
  readonly onCancel: () => void;
}) {
  return (
    <>
      <CardContent className="grid gap-3 py-3 sm:grid-cols-2 lg:grid-cols-5">
        <Field className="lg:col-span-2">
          <FieldLabel htmlFor="topic-draft-name">Name</FieldLabel>
          <Input
            id="topic-draft-name"
            type="text"
            value={draft.name}
            aria-label="Topic name"
            placeholder="Name"
            onChange={(event) => {
              onChange({...draft, name: event.target.value});
            }}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="topic-draft-status">Status</FieldLabel>
          <NativeSelect
            id="topic-draft-status"
            wrapperClassName="w-full"
            value={draft.status}
            aria-label="Status"
            onChange={(event) => {
              onChange({...draft, status: event.target.value as TopicStatus});
            }}>
            {STATUSES.map((status) => (
              <NativeSelectOption key={status} value={status}>
                {status}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Field>
          <FieldLabel htmlFor="topic-draft-start">Start</FieldLabel>
          <Input
            id="topic-draft-start"
            type="date"
            value={draft.start_date}
            aria-label="Start date"
            onChange={(event) => {
              onChange({...draft, start_date: event.target.value});
            }}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="topic-draft-end">End</FieldLabel>
          <Input
            id="topic-draft-end"
            type="date"
            value={draft.end_date}
            aria-label="End date"
            onChange={(event) => {
              onChange({...draft, end_date: event.target.value});
            }}
          />
        </Field>
        <Field className="sm:col-span-2 lg:col-span-5">
          <FieldLabel htmlFor="topic-draft-description">Description</FieldLabel>
          <Input
            id="topic-draft-description"
            type="text"
            value={draft.description}
            aria-label="Description"
            placeholder="Description (optional)"
            onChange={(event) => {
              onChange({...draft, description: event.target.value});
            }}
          />
        </Field>
      </CardContent>
      <CardFooter className="justify-end gap-2 py-3">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X /> Cancel
        </Button>
        <Button
          size="sm"
          aria-label="Save topic"
          disabled={saving}
          onClick={() => {
            void onSave();
          }}>
          <Save /> Save
        </Button>
      </CardFooter>
    </>
  );
}
